import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import {
  listTemplates, createTemplate, updateTemplate,
  listDossierDrafts, generateDocument, saveDraft, discardDraft,
  type DocumentTemplate, type GenerateDocumentInput,
} from '../../services/documents/documentService';
import {
  listPdfLayouts, setDefaultPdfLayout, applyPdfLayoutTokens, revertPdfLayoutTokens,
  type PdfLayoutRow,
} from '../../services/documents/pdfLayoutService';
import type { PdfLayoutTokens } from '../../services/documents/pdfLayoutTokens';

export const docKeys = {
  all: ['document-generation'] as const,
  templates: (activeOnly: boolean) => [...docKeys.all, 'templates', activeOnly] as const,
  drafts: (dossierId: string) => [...docKeys.all, 'drafts', dossierId] as const,
  layouts: () => [...docKeys.all, 'layouts'] as const,
};

export function useDocumentTemplates(activeOnly: boolean) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: docKeys.templates(activeOnly),
    queryFn: () => listTemplates(activeOnly),
  });
}

export function useDossierDrafts(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: docKeys.drafts(dossierId ?? ''),
    queryFn: () => listDossierDrafts(dossierId!),
    enabled: !!dossierId,
  });
}

export function useGenerateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateDocumentInput) => generateDocument(input),
    onSuccess: (_r, input) => qc.invalidateQueries({ queryKey: docKeys.drafts(input.dossierId) }),
  });
}

export function useSaveDraft(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { title?: string; content?: unknown } }) =>
      saveDraft(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.drafts(dossierId) }),
  });
}

export function useDiscardDraft(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discardDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.drafts(dossierId) }),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof createTemplate>[0]) => createTemplate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.all }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DocumentTemplate> }) =>
      updateTemplate(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.all }),
  });
}

export function usePdfLayouts() {
  return useQuery({ ...SEMI_FRESH, queryKey: docKeys.layouts(), queryFn: listPdfLayouts });
}
export function useSetDefaultPdfLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setDefaultPdfLayout(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.layouts() }),
  });
}
export function useApplyPdfLayoutTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, current, proposed }: { id: string; current: unknown; proposed: PdfLayoutTokens }) =>
      applyPdfLayoutTokens(id, current, proposed),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.layouts() }),
  });
}
export function useRevertPdfLayoutTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (row: PdfLayoutRow) => revertPdfLayoutTokens(row),
    onSuccess: () => qc.invalidateQueries({ queryKey: docKeys.layouts() }),
  });
}
